import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin } from 'lucide-react';
import { AppContext } from '../context/AppContext';
import { cn } from '../utils';

const CATEGORIES = [
  { id: 'spotted', label: 'Campus Spotted 👀', color: 'text-fuchsia-600 bg-fuchsia-100 dark:text-fuchsia-400 dark:bg-fuchsia-500/10 border border-fuchsia-500/30' },
  { id: 'drama', label: 'Drama Alert 🚨', color: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-500/10 border border-red-500/30' },
  { id: 'funny', label: 'Funny 😂', color: 'text-amber-600 bg-amber-50 dark:text-yellow-400 dark:bg-yellow-500/10' },
  { id: 'latenight', label: 'Late Night 🌙', color: 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-500/10' },
];

const UNIVERSITIES = [
  "Amity University, Noida", "Galgotias University", "JIIT Noida", "Sharda University",
  "Bennett University", "GL Bajaj", "NIET Greater Noida", "GBU Greater Noida",
  "JSS Noida", "IMS Noida", "Mangalmay", "GNIOT", "IILM Institute", "ABS Noida",
  "Accurate Institute", "Dronacharya College", "Maharishi University", "Sanjivani University", "Vishveshwarya Group",
  "GD Goenka University", "World college of technology and management",
];

export default function CreatePostModal({ isOpen, onClose, onSubmit }) {
  const [text, setText] = useState('');
  const [category, setCategory] = useState(CATEGORIES[1]);
  const [customVibe, setCustomVibe] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const { currentUser } = useContext(AppContext);
  
  // Admin fields
  const [adminUniversity, setAdminUniversity] = useState('');
  const [adminScheduleTime, setAdminScheduleTime] = useState('');
  const isAdmin = currentUser?.username === 'admin';

  const handleSubmit = () => {
    if (text.trim().length === 0) return;
    
    const finalCategory = isCustom ? customVibe.trim() : category.label;
    if (isCustom && !customVibe.trim()) return;

    const postData = { text, category: finalCategory };
    if (isAdmin) {
        if (adminUniversity.trim()) postData.university = adminUniversity.trim();
        if (adminScheduleTime) {
            postData.timestamp = new Date(adminScheduleTime).toISOString();
        }
    }

    onSubmit(postData);
    setText('');
    setCustomVibe('');
    setAdminUniversity('');
    setAdminScheduleTime('');
    setIsCustom(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm"
        />

        {/* Modal content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="relative w-full max-w-[600px] bg-white dark:bg-black sm:border border-slate-100 dark:border-slate-900/50 rounded-2xl shadow-xl overflow-hidden z-10"
        >
          <div className="flex justify-between items-center p-5 border-b border-slate-100 dark:border-slate-800/50">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">New Confession</h2>
            <button 
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-50 hover:bg-slate-100 dark:bg-slate-900 dark:hover:bg-slate-800 rounded-full transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 sm:p-6 space-y-6">
            <div className="relative">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Share what's on your mind... (100% anonymous)"
                className="w-full h-48 bg-transparent text-slate-900 dark:text-slate-100 text-lg border-b border-transparent focus:border-slate-100 dark:focus:border-slate-900/50 p-0 focus:outline-none focus:ring-0 resize-none transition-all placeholder:text-slate-300 dark:placeholder:text-slate-700"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-600 mb-3 uppercase tracking-widest">
                Select Vibe
              </label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setIsCustom(false);
                      setCategory(cat);
                    }}
                    className={cn(
                      "px-3.5 py-1.5 rounded-full text-[13px] font-bold transition-all border",
                      !isCustom && category.id === cat.id 
                        ? "bg-slate-900 dark:bg-white text-white dark:text-black border-transparent"
                        : "text-slate-500 bg-transparent border-slate-200 dark:border-slate-900/50 hover:border-slate-300 dark:hover:border-slate-700 hover:text-slate-700 dark:hover:text-slate-300"
                    )}
                  >
                    {cat.label}
                  </button>
                ))}
                <button
                  onClick={() => setIsCustom(true)}
                  className={cn(
                    "px-3.5 py-1.5 rounded-full text-[13px] font-bold transition-all border",
                    isCustom 
                      ? "bg-slate-900 dark:bg-white text-white dark:text-black border-transparent"
                      : "text-slate-400 bg-transparent border-dashed border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 hover:text-slate-600 dark:hover:text-slate-400"
                  )}
                >
                  Other +
                </button>
              </div>

              {isCustom && (
                <div className="mt-4 animate-in fade-in slide-in-from-top-1 duration-200">
                  <input 
                    type="text"
                    value={customVibe}
                    onChange={(e) => setCustomVibe(e.target.value.slice(0, 20))}
                    placeholder="Type your vibe... (e.g. Exam Mode 📚)"
                    autoFocus
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 focus:border-slate-400 dark:focus:border-slate-600 outline-none transition-all text-sm font-medium"
                  />
                  <div className="mt-1.5 px-1 flex justify-between">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Custom Badge</p>
                    <p className="text-[10px] text-slate-400">{customVibe.length}/20</p>
                  </div>
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800/50 space-y-4">
                <p className="text-[11px] font-bold text-indigo-500 uppercase tracking-widest">Admin Controls</p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-[10px] text-slate-400 mb-1">Override Campus</label>
                    <select 
                      value={adminUniversity} 
                      onChange={(e) => setAdminUniversity(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 focus:border-indigo-400 outline-none text-sm appearance-none cursor-pointer"
                    >
                      <option value="">{currentUser?.university || "Global"} (Default)</option>
                      {UNIVERSITIES.map(u => <option key={u} value={u}>{u}</option>)}
                      <option value="Global">Global</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] text-slate-400 mb-1">Schedule Post (Optional)</label>
                    <input 
                      type="datetime-local" 
                      value={adminScheduleTime} 
                      onChange={(e) => setAdminScheduleTime(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 focus:border-indigo-400 outline-none text-sm dark:text-white"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="pt-2 flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5 text-slate-400 dark:text-slate-600">
                <MapPin className="w-3.5 h-3.5" />
                <span className="text-[11px] font-bold uppercase tracking-wider">
                  {isAdmin && adminUniversity ? adminUniversity : (currentUser?.university || "Global")}
                </span>
              </div>
              <button
                onClick={handleSubmit}
                disabled={text.trim().length === 0}
                className="px-8 py-2.5 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-200 disabled:bg-slate-100 disabled:text-slate-400 dark:disabled:bg-slate-900 dark:disabled:text-slate-600 text-white dark:text-slate-900 rounded-xl font-bold transition-all"
              >
                Post
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
